/**
 * 상담 workflow 서비스 (계획서 §11). 화면은 이 서비스만 호출한다.
 *
 * 원칙
 * - 모든 쓰기는 Repository 단일 경로 (검증·불변식·암호화·감사).
 * - 여러 entity에 걸친 절차(승인 → 회기 완료 → Brief 생성)는 checkpoint로 재개 가능하게 만든다.
 *   중간에 앱이 꺼져도 다음 실행에서 `completeApproval`이 남은 단계만 수행한다 (중복 생성 없음).
 * - RAW 전사는 회기 처리 전까지만 교체할 수 있고, 교체해도 이전 원문은 soft-delete로 남는다.
 */
import { uuidv7 } from 'uuidv7';
import type {
  PsychologicalAssessment,
  AppSettings,
  VerbatimSelection,
  CaseFormulationRevision,
  Client,
  ClientIdentity,
  CounselingJournal,
  DiagnosticConsideration,
  DocumentArtifact,
  GoalStatus,
  IntakeSummary,
  NextSessionBrief,
  RiskAssessment,
  RiskItem,
  Session,
  SessionAnalysis,
  Transcript,
} from '@/domain/schema/v1';
import { RISK_KEYS } from '@/domain/schema/v1';
import { detectTranscript } from '@/domain/transcript/detect';
import { checkTranscriptPair, type InputCheck } from '@/domain/transcript/inputCheck';
import { buildBrief, type AiBriefSuggestions } from '@/domain/workflow/brief';
import { goalTrends, recurringPatterns, sessionFlow } from '@/domain/workflow/longitudinal';
import { parseSessionAnalysis } from '@/domain/ai/parse';
import { stripAiMarks } from '@/domain/ai/docDraft';
import { PROMPT_VERSIONS } from '@/domain/ai/prompts';

/** 앱 설정은 vault마다 하나 (모든 기기에서 같은 id → 중복 생성 없음) */
const SETTINGS_ID = '00000000-0000-7000-8000-000000000001';
import {
  composeCaseReport,
  composeSessionRecord,
  composeTerminationReport,
  composeDialogueRecord,
  composeVerbatimDocument,
  type CaseReportInput,
  type VerbatimBlock,
} from '@/domain/workflow/documents';
import {
  FORMULATION_ITEM_FIELDS,
  FORMULATION_LIST_FIELDS,
  nextProposal,
  parsePendingOpinions,
} from '@/domain/workflow/formulation';
import { localDate } from '@/domain/workflow/dates';
import { deriveHome, latestUnusedBrief, suggestSessionNumber } from '@/domain/workflow/home';
import { autofillJournal, type JournalFormField } from '@/domain/workflow/journal';
import { getBlobText, putBlob } from '@/data/blobs';
import { recommendVerbatims, type ExcerptLine } from '@/domain/transcript/excerpts';
import { countKeyPoints, keyPointScore, keyPointsOf, type KeyPointKind } from '@/domain/transcript/keyPoints';
import {
  approachById,
  currentStepLabel,
  techniquePattern,
  growingOthers,
  recommendApproach,
  scoreApproaches,
  type ApproachScore,
} from '@/domain/workflow/approaches';
import type { ApproachEvidence, ApproachId, TreatmentPlan } from '@/domain/schema/v1';
import { fieldBody, setFieldBody } from '@/domain/ai/docDraft';
import { LOCKED_HEADINGS, normalizeReportFormat } from '@/domain/ai/reportWriting';
import { numberDialogue } from '@/domain/workflow/docModel';
import { clientProgress, progressSummary } from '@/domain/workflow/progress';
import { tciSummary, type TciResult } from '@/domain/assessment/tci';
import { sctSummary } from '@/domain/assessment/sct';
import type { AssessmentItem } from '@/domain/schema/v1';
import { TranscriptService } from './transcripts';
import type { Draft, RepoContext, Repository } from '@/data/repo/repository';

export class WorkflowError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkflowError';
  }
}

const today = () => localDate();
const nowIso = () => new Date().toISOString();
const byCreated = <T extends { createdAt: string }>(a: T, b: T) => a.createdAt.localeCompare(b.createdAt);

export const EMPTY_PROCESSING: Session['processingState'] = {
  filesSaved: false,
  samsungParsed: false,
  appleParsed: false,
  transcriptsAligned: false,
  rolesInferred: false,
  discrepanciesDetected: false,
  workingTranscriptCreated: false,
  verificationComplete: false,
  aiPacketCreated: false,
  aiAnalysisImported: false,
  journalApproved: false,
  nextSessionBriefCreated: false,
  synced: false,
};

const NOT_ASSESSED: RiskItem = { status: 'not_assessed' };

/** 회기 진행 화면의 "확인할 것" */
const DIAGNOSIS_STATUS_KO: Record<string, string> = {
  insufficient_data: '자료 부족',
  under_review: '검토 중',
  possible: '가능성 있음',
  professional_evaluation_recommended: '전문평가 권고',
  externally_confirmed: '공식 진단 확인됨 (외부)',
};

export type SessionTodo =
  | { kind: 'risk'; id: string; present: string[]; unclear: string[] }
  | { kind: 'goal'; id: string; title: string; from: string; to: string; note?: string }
  | { kind: 'roles'; count: number }
  /** 운영 개편 v1: 사례개념화·진단적 고려 개정안도 마무리에서 승인 */
  | { kind: 'formulation'; id: string; version: number; opinions: number; changes: { label: string; text: string }[] }
  | { kind: 'diagnosis'; id: string; version: number; items: string[] };

/** 사례보고서 축어록 후보 (A32) */
export const CASE_REPORT_VERBATIM = '사례보고서 축어록';
export type VerbatimPick = {
  key: string;
  source: 'ai' | 'app';
  sessionId: string;
  sessionNumber: number;
  startMs: number;
  endMs: number;
  /** 회기 전체 길이 (시간 막대용) */
  sessionEndMs: number;
  score: number;
  points: Partial<Record<KeyPointKind, number>>;
  reason: string;
  lines: ExcerptLine[];
  chosen?: boolean;
};

/** 대화록(수업용) 추천 구간 */
export type DialogueCandidate = {
  key: string;
  source: 'ai' | 'app';
  startMs: number;
  endMs: number;
  reason?: string;
  supervisionQuestions?: string[];
  /** 구간 앞부분 내담자 발언 (고르기 쉽게) */
  preview: string;
  /** 줄마다 핵심 포인트 표시와 시간 막대용 (A32) */
  lines: ExcerptLine[];
  points: Partial<Record<KeyPointKind, number>>;
  sessionEndMs: number;
};

/** 회기를 지울 때 함께 지우는 회기 단위 자료 (다음 회기 준비·파일 참조·감사·동기화 기록은 제외) */
const SESSION_SCOPED = [
  'Transcript',
  'TranscriptDiscrepancy',
  'CounselorMemo',
  'ClinicalEvidence',
  'SessionAnalysis',
  'RiskAssessment',
  'GoalProgress',
  'CaseFormulationRevision',
  'DiagnosticConsideration',
  'CounselingJournal',
  'SupervisionNote',
  'DocumentArtifact',
  'AIImport',
  'PsychologicalAssessment',
  'VerbatimSelection',
  'CounselorReflection',
] as const;

export class CounselingService {
  constructor(
    private readonly repo: Repository,
    private readonly ctx: RepoContext,
  ) {}

  // =========================== 내담자 ===========================

  async listClients(): Promise<Client[]> {
    return (await this.repo.list('Client')).sort((a, b) => a.clientCode.localeCompare(b.clientCode));
  }

  async createClient(draft: Omit<Draft<'Client'>, 'status' | 'totalSessions'>): Promise<Client> {
    const code = draft.clientCode.trim();
    if (!code) throw new WorkflowError('내담자 코드를 입력하세요');
    if ((await this.repo.list('Client')).some((c) => c.clientCode === code)) {
      throw new WorkflowError(`이미 있는 내담자 코드입니다: ${code}`);
    }
    return this.repo.create('Client', { ...draft, clientCode: code, status: 'active', totalSessions: 0 }, 'user');
  }

  /** 내담자 코드 바꾸기 (예: 이니셜). 다른 내담자와 겹치면 거절. 이미 만든 문서 본문의 옛 코드는 그대로 둔다 (스냅샷) */
  async renameClientCode(id: string, newCode: string): Promise<Client> {
    const code = newCode.trim();
    if (!code) throw new WorkflowError('내담자 코드를 입력하세요');
    if (code.length > 32) throw new WorkflowError('내담자 코드는 32자 이내로 입력하세요');
    if ((await this.repo.list('Client')).some((c) => c.id !== id && c.clientCode === code))
      throw new WorkflowError(`이미 있는 내담자 코드입니다: ${code} (예: ${code}2)`);
    return this.repo.update('Client', id, { clientCode: code }, 'user');
  }

  updateClient(id: string, patch: Partial<Draft<'Client'>>, expectedRevision?: number) {
    return this.repo.update('Client', id, patch, 'user', expectedRevision !== undefined ? { expectedRevision } : {});
  }

  async terminateClient(
    id: string,
    t: {
      terminationDate: string;
      terminationReason: NonNullable<Client['terminationReason']>;
      terminationNote?: string;
    },
  ): Promise<Client> {
    return this.repo.update('Client', id, { status: 'terminated', ...t }, 'user');
  }

  getClient(id: string) {
    return this.repo.get('Client', id);
  }

  async ensureIdentity(clientId: string): Promise<ClientIdentity> {
    return (await this.getIdentity(clientId)) ?? this.repo.create('ClientIdentity', { clientId }, 'user');
  }

  updateIdentity(id: string, patch: Partial<Draft<'ClientIdentity'>>, expectedRevision?: number) {
    return this.repo.update(
      'ClientIdentity',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  updateIntake(id: string, patch: Partial<Draft<'IntakeSummary'>>, expectedRevision?: number) {
    return this.repo.update(
      'IntakeSummary',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  getJournal(id: string) {
    return this.repo.get('CounselingJournal', id);
  }

  getFormulationRevision(id: string) {
    return this.repo.get('CaseFormulationRevision', id);
  }

  getDiagnosis(id: string) {
    return this.repo.get('DiagnosticConsideration', id);
  }

  getAnalysisById(id: string) {
    return this.repo.get('SessionAnalysis', id);
  }

  getRiskById(id: string) {
    return this.repo.get('RiskAssessment', id);
  }

  getGoal(id: string) {
    return this.repo.get('CounselingGoal', id);
  }

  async addReflection(clientId: string, kind: 'self-understanding' | 'theological-reflection', text: string) {
    return this.repo.create('CounselorReflection', { clientId, kind, text }, 'user');
  }

  async addSupervisionQuestion(clientId: string, supervisionQuestion: string) {
    return this.repo.create('SupervisionNote', { clientId, supervisionQuestion, source: 'self-review' }, 'user');
  }

  /** 상담자 메모 고치기·지우기(휴지통)·되돌리기 (성찰·신학적 성찰은 CounselorReflection, 수퍼비전은 SupervisionNote) */
  updateNote(kind: 'self-understanding' | 'theological-reflection' | 'supervision', id: string, text: string) {
    return kind === 'supervision'
      ? this.repo.update('SupervisionNote', id, { supervisionQuestion: text }, 'user')
      : this.repo.update('CounselorReflection', id, { text }, 'user');
  }

  deleteNote(kind: 'self-understanding' | 'theological-reflection' | 'supervision', id: string) {
    return this.repo.softDelete(kind === 'supervision' ? 'SupervisionNote' : 'CounselorReflection', id);
  }

  restoreNote(kind: 'self-understanding' | 'theological-reflection' | 'supervision', id: string) {
    return this.repo.restore(kind === 'supervision' ? 'SupervisionNote' : 'CounselorReflection', id);
  }

  async listReflections(clientId: string) {
    const [reflections, supervision] = await Promise.all([
      this.repo.list('CounselorReflection', { clientId }),
      this.repo.list('SupervisionNote', { clientId }),
    ]);
    return { reflections, supervision };
  }

  async getIdentity(clientId: string): Promise<ClientIdentity | null> {
    return (await this.repo.list('ClientIdentity', { clientId })).sort(byCreated)[0] ?? null;
  }

  async saveIdentity(clientId: string, fields: Partial<Draft<'ClientIdentity'>>): Promise<ClientIdentity> {
    const cur = await this.getIdentity(clientId);
    return cur
      ? this.repo.update('ClientIdentity', cur.id, fields, 'user')
      : this.repo.create('ClientIdentity', { clientId, ...fields }, 'user');
  }

  async getIntake(clientId: string): Promise<IntakeSummary | null> {
    return (await this.repo.list('IntakeSummary', { clientId })).sort(byCreated)[0] ?? null;
  }

  async ensureIntake(clientId: string): Promise<IntakeSummary> {
    return (
      (await this.getIntake(clientId)) ??
      this.repo.create('IntakeSummary', { clientId, presentingProblems: [] }, 'user')
    );
  }

  // =========================== 회기 ===========================

  async listSessions(clientId: string): Promise<Session[]> {
    return (await this.repo.list('Session', { clientId })).sort((a, b) => a.sessionNumber - b.sessionNumber);
  }

  async suggestNextSession(clientId: string) {
    const sessions = await this.listSessions(clientId);
    return { sessionNumber: suggestSessionNumber(sessions), sessionDate: today(), durationMinutes: 50 };
  }

  async startSession(
    clientId: string,
    opts: { sessionNumber?: number; sessionDate?: string; durationMinutes?: number } = {},
  ): Promise<Session> {
    const sessions = await this.listSessions(clientId);
    const n = opts.sessionNumber ?? suggestSessionNumber(sessions);
    if (sessions.some((s) => s.sessionNumber === n)) throw new WorkflowError(`${n}회기가 이미 있습니다`);
    if (opts.durationMinutes !== undefined && opts.durationMinutes < 1)
      throw new WorkflowError('상담 시간을 확인하세요');
    return this.repo.create(
      'Session',
      {
        clientId,
        sessionNumber: n,
        sessionDate: opts.sessionDate ?? today(),
        durationMinutes: opts.durationMinutes ?? 50,
        modality: 'individual',
        status: 'draft',
        processingState: { ...EMPTY_PROCESSING },
      },
      'user',
    );
  }

  getSession(id: string) {
    return this.repo.get('Session', id);
  }

  updateSession(id: string, patch: Partial<Draft<'Session'>>, expectedRevision?: number) {
    return this.repo.update('Session', id, patch, 'user', expectedRevision !== undefined ? { expectedRevision } : {});
  }

  async deleteSession(id: string) {
    const s = await this.getSession(id);
    if (s.status === 'approved') throw new WorkflowError('승인된 회기는 삭제할 수 없습니다');
    // 이 회기에서 나온 자료(전사·메모·분석·근거·위험·AI 결과·기록지 초안·후보 구간 등)도 함께 지운다.
    // 지운 회기의 자료가 사례개념화·검색·문서에 남지 않게. 승인된 것은 건드리지 않는다. 모두 soft-delete라 이력에 남는다
    for (const type of SESSION_SCOPED) {
      for (const e of await this.repo.list(type, { sessionId: id })) {
        const x = e as { status?: string; approvedAt?: string };
        if (x.status === 'approved' || x.approvedAt) continue;
        await this.repo.softDelete(type, e.id);
      }
    }
    await this.repo.softDelete('Session', id);
  }

  /** RAW 전사 저장. 같은 내용이면 아무것도 하지 않는다 (재시도·자동저장 멱등). */
  async saveRawTranscript(
    sessionId: string,
    source: 'samsung_raw' | 'apple_raw',
    text: string,
  ): Promise<Transcript | null> {
    const session = await this.getSession(sessionId);
    const field = source === 'samsung_raw' ? 'samsungTranscriptId' : 'appleTranscriptId';
    const currentId = session[field];
    const current = currentId ? await this.repo.find('Transcript', currentId) : null;
    if (current?.rawBlobId && (await getBlobText(this.ctx, current.rawBlobId)) === text) return current;
    if (session.status !== 'draft' || session.processingState.filesSaved) {
      throw new WorkflowError('회기 처리 후에는 원본 전사를 바꿀 수 없습니다 (원본 보존)');
    }
    if (current) await this.repo.softDelete('Transcript', current.id);
    if (!text.trim()) {
      // 패치의 undefined 값은 필드 제거로 처리된다
      await this.repo.update('Session', sessionId, { [field]: undefined } as Partial<Draft<'Session'>>, 'user');
      return null;
    }
    const blobId = await putBlob(this.ctx, text);
    const t = await this.repo.create(
      'Transcript',
      {
        clientId: session.clientId,
        sessionId,
        source,
        language: 'ko',
        originalFormat: detectTranscript(text).kind,
        rawBlobId: blobId,
        parseStatus: 'pending',
        segments: [],
      },
      'user',
    );
    await this.repo.update('Session', sessionId, { [field]: t.id } as Partial<Draft<'Session'>>, 'user');
    return t;
  }

  // ---------- 회기 진행 화면 (A31) ----------

  /** 회기 번호 바꾸기: 같은 내담자의 다른 회기와 겹치면 막는다. 다음 회기 번호는 "가장 큰 번호 + 1"로 자동 */
  async setSessionNumber(sessionId: string, n: number): Promise<Session> {
    if (!Number.isInteger(n) || n < 1 || n > 500) throw new WorkflowError('회기 번호를 확인하세요');
    const s = await this.getSession(sessionId);
    if (s.sessionNumber === n) return s;
    const others = (await this.listSessions(s.clientId)).filter((x) => x.id !== s.id);
    if (others.some((x) => x.sessionNumber === n)) throw new WorkflowError(`${n}회기가 이미 있습니다`);
    return this.repo.update('Session', sessionId, { sessionNumber: n }, 'user');
  }

  /** 날짜 순서로 회기 번호를 다시 매긴다 (같은 날짜는 만든 순서) */
  async renumberSessionsByDate(clientId: string): Promise<void> {
    const sessions = (await this.listSessions(clientId)).sort(
      (a, b) => a.sessionDate.localeCompare(b.sessionDate) || a.createdAt.localeCompare(b.createdAt),
    );
    // 겹치지 않게 먼저 큰 번호로 옮긴 뒤 1부터 매긴다
    for (const [k, x] of sessions.entries())
      if (x.sessionNumber !== k + 1) await this.repo.update('Session', x.id, { sessionNumber: 1000 + k }, 'user');
    for (const [k, x] of sessions.entries()) {
      const cur = await this.getSession(x.id);
      if (cur.sessionNumber !== k + 1) await this.repo.update('Session', x.id, { sessionNumber: k + 1 }, 'user');
    }
  }

  /** 날짜로 본 회기 번호가 지금 번호와 다른지 (다르면 화면에서 알려준다) */
  async dateOrderNumber(sessionId: string): Promise<number> {
    const s = await this.getSession(sessionId);
    const sorted = (await this.listSessions(s.clientId)).sort(
      (a, b) => a.sessionDate.localeCompare(b.sessionDate) || a.createdAt.localeCompare(b.createdAt),
    );
    return sorted.findIndex((x) => x.id === s.id) + 1;
  }

  /** 1단계: 두 기기 전사가 들어왔는지·문제가 없는지 */
  async transcriptInputCheck(sessionId: string): Promise<InputCheck & { galaxyText: string; iphoneText: string }> {
    const s = await this.getSession(sessionId);
    const [galaxyText, iphoneText] = await Promise.all([
      this.getRawTranscriptText(s.samsungTranscriptId),
      this.getRawTranscriptText(s.appleTranscriptId),
    ]);
    return { ...checkTranscriptPair(galaxyText, iphoneText), galaxyText, iphoneText };
  }

  /** 3단계: AI 반영 뒤 상담자가 확인할 것 (위험 승인, 목표 진행, AI가 고친 화자, 연결 못한 가설 의견) */
  async sessionTodo(sessionId: string): Promise<SessionTodo[]> {
    const s = await this.getSession(sessionId);
    const out: SessionTodo[] = [];
    const risk = await this.getRisk(sessionId);
    if (risk && !risk.counselorApproved) {
      const marked = RISK_KEYS.filter((k) => risk[k] && risk[k].status !== 'not_assessed');
      if (marked.length)
        out.push({
          kind: 'risk',
          id: risk.id,
          present: marked.filter((k) => risk[k].status === 'present'),
          unclear: marked.filter((k) => risk[k].status === 'unclear'),
        });
    }
    const goals = await this.listGoals(s.clientId);
    for (const g of (await this.listGoalProgress({ sessionId })).filter(
      (x) => !x.deletedAt && x.proposedStatus && !x.approvedStatus,
    )) {
      const goal = goals.find((x) => x.id === g.goalId);
      if (goal && goal.status !== g.proposedStatus)
        out.push({
          kind: 'goal',
          id: g.id,
          title: goal.title,
          from: goal.status,
          to: g.proposedStatus!,
          ...(g.progressNote ? { note: g.progressNote } : {}),
        });
    }
    const w = await new TranscriptService(this.repo, this.ctx).getWorking(s);
    const fixed = (w?.segments ?? []).filter((x) => x.roleSource === 'ai-review').length;
    if (fixed) out.push({ kind: 'roles', count: fixed });
    const proposal = (await this.listFormulationRevisions(s.clientId)).find((x) => x.status === 'proposal');
    if (proposal) {
      const all = FORMULATION_ITEM_FIELDS.flatMap((f) => proposal[f] ?? []);
      const textOf = (id: string) => all.find((x) => x.id === id)?.text;
      const changes = (
        [
          ['추가', proposal.addedHypotheses],
          ['강화', proposal.strengthenedHypotheses],
          ['약화', proposal.weakenedHypotheses],
          ['폐기', proposal.rejectedHypotheses],
        ] as const
      ).flatMap(([label, ids]) => (ids ?? []).map((id) => ({ label, text: textOf(id) ?? '' })).filter((x) => x.text));
      out.push({
        kind: 'formulation',
        id: proposal.id,
        version: proposal.version,
        opinions: parsePendingOpinions(proposal.changeReason).length,
        changes,
      });
    }
    const dx = (await this.listDiagnosis(s.clientId)).find((x) => x.status === 'proposal');
    if (dx)
      out.push({
        kind: 'diagnosis',
        id: dx.id,
        version: dx.version,
        items: dx.considerations.map((c) => `${c.diagnosisName} (${DIAGNOSIS_STATUS_KO[c.status] ?? c.status})`),
      });
    return out;
  }

  /**
   * 회기별 아직 승인하지 않은 것의 수 (진행 막대의 "검토·승인" 단계용, 가벼운 계산).
   * 위험·목표 진행은 그 회기에, 사례개념화·진단 개정안은 AI를 반영한 가장 최근 회기에 센다.
   */
  async pendingApprovals(clientId: string): Promise<Record<string, number>> {
    const [sessions, risks, progress, goals, revisions, dx] = await Promise.all([
      this.listSessions(clientId),
      this.repo.list('RiskAssessment', { clientId }),
      this.repo.list('GoalProgress', { clientId }),
      this.listGoals(clientId),
      this.listFormulationRevisions(clientId),
      this.listDiagnosis(clientId),
    ]);
    const out: Record<string, number> = {};
    const add = (id: string | undefined) => {
      if (id) out[id] = (out[id] ?? 0) + 1;
    };
    for (const r of risks)
      if (!r.deletedAt && !r.counselorApproved && RISK_KEYS.some((k) => r[k] && r[k].status !== 'not_assessed'))
        add(r.sessionId);
    for (const g of progress) {
      const goal = goals.find((x) => x.id === g.goalId);
      if (!g.deletedAt && g.proposedStatus && !g.approvedStatus && goal && goal.status !== g.proposedStatus)
        add(g.sessionId);
    }
    const latest = sessions.filter((s) => s.processingState.aiAnalysisImported).at(-1)?.id;
    if (revisions.some((x) => x.status === 'proposal')) add(latest);
    if (dx.some((x) => x.status === 'proposal')) add(latest);
    return out;
  }

  /**
   * AI가 고친 화자 줄 목록: 시각·발언·지금 화자·AI가 댄 이유·앞뒤 한 줄 (확인 목록에서 줄마다 고치기 위해).
   * 이유는 이 회기 AI 결과의 roleReviewProposals에서 (발언 번호 g12로 온 것도 g12-0 등에 연결).
   */
  async aiRoleFixes(sessionId: string): Promise<AiRoleFix[]> {
    const s = await this.getSession(sessionId);
    const w = await new TranscriptService(this.repo, this.ctx).getWorking(s);
    if (!w) return [];
    const reasons = new Map<string, string>();
    const imports = (await this.repo.list('AIImport', { sessionId }))
      .filter((i) => !i.deletedAt && i.purpose === 'session-analysis')
      .sort(byCreated);
    for (const imp of imports)
      for (const p of imp.roleReviewProposals ?? []) if (p.reason) reasons.set(p.segmentId, p.reason);
    const turn = (id: string) => id.replace(/-\d+$/, '');
    const segs = w.segments;
    const ctx = (i: number) => {
      const x = segs[i];
      return x ? { role: x.clinicalRole, text: x.text } : undefined;
    };
    const out: AiRoleFix[] = [];
    segs.forEach((x, i) => {
      if (x.roleSource !== 'ai-review') return;
      const reason = reasons.get(x.segmentId) ?? reasons.get(turn(x.segmentId));
      const prev = ctx(i - 1);
      const next = ctx(i + 1);
      out.push({
        segmentId: x.segmentId,
        startMs: x.startMs ?? 0,
        text: x.text,
        role: x.clinicalRole,
        ...(reason ? { reason } : {}),
        ...(prev ? { prev } : {}),
        ...(next ? { next } : {}),
      });
    });
    return out;
  }

  /** AI가 고친 화자를 상담자가 그대로 두기로 확인 (상담자 확인 화자로) */
  async acceptAiRoleFixes(sessionId: string): Promise<number> {
    const s = await this.getSession(sessionId);
    const w = await new TranscriptService(this.repo, this.ctx).getWorking(s);
    if (!w) return 0;
    let n = 0;
    const segments = w.segments.map((x) => {
      if (x.roleSource !== 'ai-review') return x;
      n++;
      return { ...x, roleSource: 'user' as const, roleConfidence: 1 };
    });
    if (n) await this.repo.update('Transcript', w.id, { segments }, 'user');
    return n;
  }

  async getRawTranscriptText(transcriptId: string | undefined): Promise<string> {
    if (!transcriptId) return '';
    const t = await this.repo.find('Transcript', transcriptId);
    return t?.rawBlobId ? getBlobText(this.ctx, t.rawBlobId) : '';
  }

  async getMemo(sessionId: string) {
    return (await this.repo.list('CounselorMemo', { sessionId })).sort(byCreated)[0] ?? null;
  }

  async ensureMemo(sessionId: string) {
    return (await this.getMemo(sessionId)) ?? this.saveMemo(sessionId, '');
  }

  /** 상담자 메모: 항상 COUNSELOR_OBSERVATION (MASTER §5.2) */
  async saveMemo(sessionId: string, text: string, expectedRevision?: number) {
    const session = await this.getSession(sessionId);
    const cur = await this.getMemo(sessionId);
    if (cur)
      return this.repo.update(
        'CounselorMemo',
        cur.id,
        { text },
        'user',
        expectedRevision !== undefined ? { expectedRevision } : {},
      );
    const memo = await this.repo.create(
      'CounselorMemo',
      { clientId: session.clientId, sessionId, text, inputType: 'text', evidenceType: 'COUNSELOR_OBSERVATION' },
      'user',
    );
    await this.repo.update('Session', sessionId, { counselorMemoIds: [memo.id] }, 'user');
    return memo;
  }

  /**
   * 회기 처리 (UX_FLOW §9). checkpoint 기반이라 몇 번을 다시 눌러도 안전하다.
   * M2: 자료 저장 확인까지. 파싱·정렬·화자 판정(M4)은 해당 단계가 생기면 이어서 실행된다.
   */
  async processSession(sessionId: string): Promise<Session> {
    let s = await this.getSession(sessionId);
    if (s.status === 'approved' || s.status === 'review') return s;
    if (
      (s.status === 'ai_pending' || s.status === 'verification') &&
      (s.processingState.workingTranscriptCreated || (!s.samsungTranscriptId && !s.appleTranscriptId))
    )
      return s;
    const memo = await this.getMemo(sessionId);
    const hasInput = !!s.samsungTranscriptId || !!s.appleTranscriptId || !!memo?.text.trim();
    if (!hasInput) throw new WorkflowError('전사나 상담자 메모 중 하나는 있어야 합니다');

    if (!s.processingState.filesSaved) {
      // 원문이 실제로 끝까지 읽히는지 확인한 뒤에만 저장 완료로 표시
      await this.getRawTranscriptText(s.samsungTranscriptId);
      await this.getRawTranscriptText(s.appleTranscriptId);
      s = await this.repo.update(
        'Session',
        sessionId,
        { status: 'processing', processingState: { ...s.processingState, filesSaved: true } },
        'system',
      );
    }
    // 파싱 → 정렬 → 화자 판정 → 불일치 → Working Transcript (멈춰도 같은 결과로 이어서)
    if ((s.samsungTranscriptId || s.appleTranscriptId) && !s.processingState.workingTranscriptCreated) {
      const { working, needsReview } = await new TranscriptService(this.repo, this.ctx).buildWorking(s);
      s = await this.repo.update(
        'Session',
        sessionId,
        {
          workingTranscriptId: working.id,
          status: needsReview > 0 ? 'verification' : 'ai_pending',
          processingState: {
            ...s.processingState,
            samsungParsed: !!s.samsungTranscriptId,
            appleParsed: !!s.appleTranscriptId,
            transcriptsAligned: !!s.samsungTranscriptId && !!s.appleTranscriptId,
            rolesInferred: true,
            discrepanciesDetected: true,
            workingTranscriptCreated: true,
            verificationComplete: needsReview === 0,
          },
        },
        'system',
      );
    }
    if (s.status === 'processing' || s.status === 'draft') {
      s = await this.repo.update('Session', sessionId, { status: 'ai_pending' }, 'system');
    }
    return s;
  }

  /** 중요 전사 확인 완료 (남은 항목이 있어도 상담자가 진행을 선택할 수 있다: 남은 항목은 확인 필요로 계속 표시) */
  async completeVerification(sessionId: string): Promise<Session> {
    const s = await this.getSession(sessionId);
    return this.repo.update(
      'Session',
      sessionId,
      {
        ...(s.status === 'verification' ? { status: 'ai_pending' as const } : {}),
        processingState: { ...s.processingState, verificationComplete: true },
      },
      'user',
    );
  }

  // =========================== 회기 분석 ===========================

  async getAnalysis(sessionId: string): Promise<SessionAnalysis | null> {
    return (await this.repo.list('SessionAnalysis', { sessionId })).sort(byCreated).at(-1) ?? null;
  }

  async ensureAnalysis(sessionId: string): Promise<SessionAnalysis> {
    const cur = await this.getAnalysis(sessionId);
    if (cur) return cur;
    const s = await this.getSession(sessionId);
    return this.repo.create(
      'SessionAnalysis',
      { clientId: s.clientId, sessionId, formatVersion: 'SESSION_ANALYSIS_V1', source: 'manual' },
      'user',
    );
  }

  updateAnalysis(id: string, patch: Partial<Draft<'SessionAnalysis'>>, expectedRevision?: number) {
    return this.repo.update(
      'SessionAnalysis',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  async addEvidence(
    sessionId: string,
    e: Pick<Draft<'ClinicalEvidence'>, 'evidenceType' | 'text'> & { category?: string },
  ) {
    const s = await this.getSession(sessionId);
    return this.repo.create(
      'ClinicalEvidence',
      {
        clientId: s.clientId,
        sessionId,
        sourceSession: s.sessionNumber,
        evidenceType: e.evidenceType,
        text: e.text,
        ...(e.category ? { category: e.category } : {}),
        source: e.evidenceType === 'COUNSELOR_OBSERVATION' ? 'counselor-memo' : 'user-entry',
        verified: e.evidenceType === 'FACT',
        needsVerification: false,
        author: 'counselor',
      },
      'user',
    );
  }

  /** 상담자 확인: 이 문장이 실제 내담자 진술임을 상담자가 확인 (사례보고서 I의 내담자 표현에 쓰임) */
  setEvidenceVerified(id: string, verified: boolean) {
    return this.repo.update(
      'ClinicalEvidence',
      id,
      verified ? { verified: true, needsVerification: false } : { verified: false },
      'user',
    );
  }

  listEvidence(filter: { clientId?: string; sessionId?: string }) {
    return this.repo.list('ClinicalEvidence', filter);
  }

  // =========================== 위험도 ===========================

  async getRisk(sessionId: string): Promise<RiskAssessment | null> {
    return (await this.repo.list('RiskAssessment', { sessionId })).sort(byCreated).at(-1) ?? null;
  }

  async ensureRisk(sessionId: string): Promise<RiskAssessment> {
    const cur = await this.getRisk(sessionId);
    if (cur) return cur;
    const s = await this.getSession(sessionId);
    const items = Object.fromEntries(RISK_KEYS.map((k) => [k, { ...NOT_ASSESSED }])) as Record<
      (typeof RISK_KEYS)[number],
      RiskItem
    >;
    return this.repo.create(
      'RiskAssessment',
      { clientId: s.clientId, sessionId, ...items, counselorApproved: false },
      'user',
    );
  }

  updateRisk(id: string, patch: Partial<Draft<'RiskAssessment'>>, expectedRevision?: number) {
    return this.repo.update(
      'RiskAssessment',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  approveRisk(id: string) {
    return this.repo.update('RiskAssessment', id, { counselorApproved: true }, 'user');
  }

  /** 승인된 위험도를 고쳐야 할 때: 새 기록 (이전 승인 기록은 보존) */
  async reviseRisk(id: string): Promise<RiskAssessment> {
    const old = await this.repo.get('RiskAssessment', id);
    const { clientId, sessionId, overallNote } = old;
    const items = Object.fromEntries(RISK_KEYS.map((k) => [k, structuredClone(old[k])]));
    return this.repo.create(
      'RiskAssessment',
      {
        clientId,
        sessionId,
        ...(overallNote ? { overallNote } : {}),
        ...items,
        counselorApproved: false,
      } as Draft<'RiskAssessment'>,
      'user',
    );
  }

  // =========================== 상담목표 ===========================

  async listGoals(clientId: string) {
    return (await this.repo.list('CounselingGoal', { clientId })).sort(byCreated);
  }

  createGoal(clientId: string, g: Omit<Draft<'CounselingGoal'>, 'clientId' | 'status'> & { status?: GoalStatus }) {
    return this.repo.create('CounselingGoal', { status: 'not_started', ...g, clientId }, 'user');
  }

  updateGoal(id: string, patch: Partial<Draft<'CounselingGoal'>>, expectedRevision?: number) {
    return this.repo.update(
      'CounselingGoal',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  async listGoalProgress(filter: { clientId?: string; sessionId?: string }) {
    return (await this.repo.list('GoalProgress', filter)).sort(byCreated);
  }

  async saveGoalProgress(goalId: string, sessionId: string, p: { progressNote?: string; proposedStatus?: GoalStatus }) {
    const s = await this.getSession(sessionId);
    const existing = (await this.listGoalProgress({ sessionId }))
      .filter((x) => x.goalId === goalId && !x.approvedStatus)
      .at(-1);
    return existing
      ? this.repo.update('GoalProgress', existing.id, p, 'user')
      : this.repo.create('GoalProgress', { clientId: s.clientId, goalId, sessionId, ...p }, 'user');
  }

  /** 목표 상태 변경 승인: 진행 기록을 동결하고 목표 상태를 바꾼다 */
  async approveGoalProgress(progressId: string) {
    const p = await this.repo.get('GoalProgress', progressId);
    if (!p.proposedStatus) throw new WorkflowError('제안된 상태가 없습니다');
    const goal = await this.repo.get('CounselingGoal', p.goalId);
    if (goal.status !== p.proposedStatus) {
      const s = await this.getSession(p.sessionId);
      await this.repo.update(
        'CounselingGoal',
        goal.id,
        { status: p.proposedStatus, relatedSessionIds: [...new Set([...(goal.relatedSessionIds ?? []), s.id])] },
        'user',
      );
    }
    return this.repo.update('GoalProgress', progressId, { approvedStatus: p.proposedStatus }, 'user');
  }

  // =========================== 사례개념화 ===========================

  async ensureFormulation(clientId: string) {
    return (
      (await this.repo.list('CaseFormulation', { clientId }))[0] ??
      this.repo.create('CaseFormulation', { clientId }, 'user')
    );
  }

  async listFormulationRevisions(clientId: string): Promise<CaseFormulationRevision[]> {
    return (await this.repo.list('CaseFormulationRevision', { clientId })).sort((a, b) => a.version - b.version);
  }

  async currentFormulation(clientId: string): Promise<CaseFormulationRevision | undefined> {
    const f = (await this.repo.list('CaseFormulation', { clientId }))[0];
    return f?.currentRevisionId
      ? ((await this.repo.find('CaseFormulationRevision', f.currentRevisionId)) ?? undefined)
      : undefined;
  }

  /** 새 revision 제안: 최신 revision을 복제해 시작. 진행 중인 제안이 있으면 그것을 돌려준다. */
  async createFormulationProposal(clientId: string, basedOnSessionIds: string[]): Promise<CaseFormulationRevision> {
    const revisions = await this.listFormulationRevisions(clientId);
    const open = revisions.find((r) => r.status === 'proposal');
    if (open) return open;
    const formulation = await this.ensureFormulation(clientId);
    const prev = (await this.currentFormulation(clientId)) ?? revisions.at(-1);
    const version = (revisions.at(-1)?.version ?? 0) + 1;
    return this.repo.create(
      'CaseFormulationRevision',
      nextProposal(prev, { clientId, formulationId: formulation.id, version, basedOnSessionIds }),
      'user',
    );
  }

  updateFormulation(id: string, patch: Partial<Draft<'CaseFormulationRevision'>>, expectedRevision?: number) {
    return this.repo.update(
      'CaseFormulationRevision',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  /** 승인 전 개정안 버리기 (휴지통: 되돌릴 수 있음). 승인된 버전은 버릴 수 없다 */
  async discardFormulationProposal(id: string) {
    const rev = await this.repo.get('CaseFormulationRevision', id);
    if (rev.status !== 'proposal') throw new WorkflowError('승인된 버전은 버릴 수 없습니다');
    await this.repo.softDelete('CaseFormulationRevision', id);
  }

  /** 버린 개정안 (되돌릴 수 있게 보여준다) */
  async discardedFormulationProposals(clientId: string): Promise<CaseFormulationRevision[]> {
    return (await this.repo.list('CaseFormulationRevision', { clientId, includeDeleted: true }))
      .filter((r) => r.deletedAt && r.status === 'proposal')
      .sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));
  }

  async restoreFormulationProposal(id: string) {
    const rev = await this.repo.get('CaseFormulationRevision', id, { includeDeleted: true });
    const open = (await this.listFormulationRevisions(rev.clientId)).find((r) => r.status === 'proposal');
    if (open) throw new WorkflowError('지금 다른 개정안이 있어 되돌릴 수 없습니다');
    await this.repo.restore('CaseFormulationRevision', id);
  }

  async discardDiagnosisProposal(id: string) {
    const d = await this.repo.get('DiagnosticConsideration', id);
    if (d.status !== 'proposal') throw new WorkflowError('승인된 버전은 버릴 수 없습니다');
    await this.repo.softDelete('DiagnosticConsideration', id);
  }

  async discardedDiagnosisProposals(clientId: string): Promise<DiagnosticConsideration[]> {
    return (await this.repo.list('DiagnosticConsideration', { clientId, includeDeleted: true }))
      .filter((d) => d.deletedAt && d.status === 'proposal')
      .sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));
  }

  async restoreDiagnosisProposal(id: string) {
    const d = await this.repo.get('DiagnosticConsideration', id, { includeDeleted: true });
    if ((await this.listDiagnosis(d.clientId)).some((x) => x.status === 'proposal'))
      throw new WorkflowError('지금 다른 개정안이 있어 되돌릴 수 없습니다');
    await this.repo.restore('DiagnosticConsideration', id);
  }

  async approveFormulation(id: string): Promise<CaseFormulationRevision> {
    const rev = await this.repo.get('CaseFormulationRevision', id);
    // 비워 둔 줄은 승인할 때 뺀다 (보고서에 빈 항목이 나오지 않게)
    const clean = Object.fromEntries([
      ...FORMULATION_LIST_FIELDS.filter((k) => rev[k]?.some((t) => !t.trim())).map((k) => [
        k,
        rev[k]!.map((t) => t.trim()).filter(Boolean),
      ]),
      ...FORMULATION_ITEM_FIELDS.filter((k) => rev[k]?.some((x) => !x.text.trim())).map((k) => [
        k,
        rev[k]!.filter((x) => x.text.trim()),
      ]),
    ]);
    const approved =
      rev.status === 'approved'
        ? rev
        : await this.repo.update(
            'CaseFormulationRevision',
            id,
            { ...clean, status: 'approved', approvedByCounselor: true, approvedAt: nowIso() },
            'user',
          );
    const f = await this.repo.get('CaseFormulation', rev.formulationId);
    if (f.currentRevisionId !== id) await this.repo.update('CaseFormulation', f.id, { currentRevisionId: id }, 'user');
    return approved;
  }

  // =========================== 진단적 고려 ===========================

  async listDiagnosis(clientId: string): Promise<DiagnosticConsideration[]> {
    return (await this.repo.list('DiagnosticConsideration', { clientId })).sort((a, b) => a.version - b.version);
  }

  async currentDiagnosis(clientId: string) {
    return (await this.listDiagnosis(clientId)).filter((d) => d.status === 'approved').at(-1);
  }

  async createDiagnosisProposal(clientId: string, basedOnSessionIds: string[] = []) {
    const all = await this.listDiagnosis(clientId);
    const open = all.find((d) => d.status === 'proposal');
    if (open) return open;
    const prev = all.filter((d) => d.status === 'approved').at(-1);
    return this.repo.create(
      'DiagnosticConsideration',
      {
        clientId,
        version: (all.at(-1)?.version ?? 0) + 1,
        ...(prev ? { previousVersionId: prev.id } : {}),
        basedOnSessionIds,
        considerations: structuredClone(prev?.considerations ?? []),
        ...(prev?.symptomList ? { symptomList: [...prev.symptomList] } : {}),
        ...(prev?.recommendedAssessment ? { recommendedAssessment: [...prev.recommendedAssessment] } : {}),
        status: 'proposal',
      },
      'user',
    );
  }

  updateDiagnosis(id: string, patch: Partial<Draft<'DiagnosticConsideration'>>, expectedRevision?: number) {
    return this.repo.update(
      'DiagnosticConsideration',
      id,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  async approveDiagnosis(id: string) {
    // 비워 둔 줄은 승인할 때 뺀다
    const d = await this.repo.get('DiagnosticConsideration', id);
    const clean = (x?: string[]) => x?.map((t) => t.trim()).filter(Boolean);
    const symptomList = clean(d.symptomList);
    const recommendedAssessment = clean(d.recommendedAssessment);
    return this.repo.update(
      'DiagnosticConsideration',
      id,
      {
        ...(symptomList ? { symptomList } : {}),
        ...(recommendedAssessment ? { recommendedAssessment } : {}),
        considerations: d.considerations.map((c) => {
          const criteriaToAssess = clean(c.criteriaToAssess);
          const differentialConsiderations = clean(c.differentialConsiderations);
          return {
            ...c,
            ...(criteriaToAssess ? { criteriaToAssess } : {}),
            ...(differentialConsiderations ? { differentialConsiderations } : {}),
          };
        }),
        status: 'approved',
        approvedAt: nowIso(),
      },
      'user',
    );
  }

  // =========================== 상담기록지 ===========================

  async listJournals(filter: { clientId?: string; sessionId?: string }) {
    return (await this.repo.list('CounselingJournal', filter)).sort(byCreated);
  }

  /** 회기의 현재 상담기록지 = 개정되지 않은 최신본 */
  async currentJournal(sessionId: string): Promise<CounselingJournal | null> {
    return (await this.listJournals({ sessionId })).filter((j) => !j.supersededById).at(-1) ?? null;
  }

  async previousBriefFor(session: Session): Promise<NextSessionBrief | undefined> {
    const briefs = await this.repo.list('NextSessionBrief', { clientId: session.clientId });
    return briefs
      .filter((b) => b.targetSessionNumber === session.sessionNumber)
      .sort(byCreated)
      .at(-1);
  }

  /** 상담기록지 초안 (자동 채움). 이미 있으면 그대로 반환 (멱등). */
  async createJournalDraft(sessionId: string): Promise<CounselingJournal> {
    const existing = await this.currentJournal(sessionId);
    if (existing) return existing;
    const session = await this.getSession(sessionId);
    const client = await this.repo.get('Client', session.clientId);
    const [intake, analysis, goals, assessments, memos, previousBrief] = await Promise.all([
      this.getIntake(client.id),
      this.getAnalysis(sessionId),
      this.listGoals(client.id),
      this.repo.list('PsychologicalAssessment', { clientId: client.id }),
      this.repo.list('CounselorMemo', { sessionId }),
      this.previousBriefFor(session),
    ]);
    const { fields, fieldSources } = autofillJournal({
      client,
      session,
      goals,
      assessments,
      memos,
      ...(intake ? { intake } : {}),
      ...(analysis ? { analysis } : {}),
      ...(previousBrief ? { previousBrief } : {}),
    });
    const journal = await this.repo.create(
      'CounselingJournal',
      { clientId: client.id, sessionId, ...fields, fieldSources, status: 'draft' },
      'user',
    );
    if (session.status !== 'approved') await this.repo.update('Session', sessionId, { status: 'review' }, 'user');
    return journal;
  }

  async updateJournal(
    id: string,
    patch: Partial<Draft<'CounselingJournal'>>,
    editedFields: string[],
    expectedRevision?: number,
  ) {
    const cur = await this.repo.get('CounselingJournal', id);
    const fieldSources = {
      ...(cur.fieldSources ?? {}),
      ...Object.fromEntries(editedFields.map((k) => [k, 'user' as const])),
    };
    return this.repo.update(
      'CounselingJournal',
      id,
      { ...patch, fieldSources },
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  markJournalReviewed(id: string) {
    return this.repo.update('CounselingJournal', id, { status: 'reviewed' }, 'user');
  }

  /** 상담기록지 승인 → 회기 완료 → 다음 회기 Brief. 각 단계 멱등, 중단 후 재실행 안전. */
  async approveJournal(id: string): Promise<{ journal: CounselingJournal; brief: NextSessionBrief }> {
    const j = await this.repo.get('CounselingJournal', id);
    const journal =
      j.status === 'approved'
        ? j
        : await this.repo.update('CounselingJournal', id, { status: 'approved', approvedAt: nowIso() }, 'user');
    const brief = await this.completeApproval(journal.sessionId);
    return { journal, brief };
  }

  /** 승인 이후 단계의 재개 지점 (회기 화면을 열 때도 호출해 중단된 절차를 마무리한다) */
  async completeApproval(sessionId: string): Promise<NextSessionBrief> {
    let session = await this.getSession(sessionId);
    const journal = await this.currentJournal(sessionId);
    if (!journal || journal.status !== 'approved') throw new WorkflowError('승인된 상담기록지가 없습니다');

    if (session.status !== 'approved' || !session.processingState.journalApproved) {
      session = await this.repo.update(
        'Session',
        sessionId,
        { status: 'approved', processingState: { ...session.processingState, journalApproved: true } },
        'system',
      );
    }
    await this.refreshClientCounters(session.clientId);
    const brief = await this.generateBrief(sessionId);
    if (!session.processingState.nextSessionBriefCreated) {
      await this.repo.update(
        'Session',
        sessionId,
        { processingState: { ...session.processingState, nextSessionBriefCreated: true } },
        'system',
      );
    }
    return brief;
  }

  /** 승인 이후에도 회기가 미완료 상태로 남아 있으면 마무리한다 (중단 복구) */
  async resumeIfNeeded(sessionId: string): Promise<boolean> {
    const s = await this.getSession(sessionId);
    const j = await this.currentJournal(sessionId);
    if (j?.status === 'approved' && (s.status !== 'approved' || !s.processingState.nextSessionBriefCreated)) {
      await this.completeApproval(sessionId);
      return true;
    }
    return false;
  }

  /** 승인본 개정: 원본은 그대로, 개정본(draft)을 새로 만든다 */
  async reviseJournal(id: string): Promise<CounselingJournal> {
    const old = await this.repo.get('CounselingJournal', id);
    if (old.status !== 'approved') throw new WorkflowError('승인된 기록만 개정본을 만듭니다');
    if (old.supersededById) return this.repo.get('CounselingJournal', old.supersededById);
    const copy = Object.fromEntries(
      Object.entries(old).filter(
        ([k]) =>
          ![
            'id',
            'entityType',
            'schemaVersion',
            'revision',
            'createdAt',
            'updatedAt',
            'createdBy',
            'updatedBy',
            'lastModifiedDevice',
            'deletedAt',
            'status',
            'approvedAt',
            'supersededById',
            'previousVersionId',
          ].includes(k),
      ),
    ) as Draft<'CounselingJournal'>;
    const draft = await this.repo.create(
      'CounselingJournal',
      { ...copy, status: 'draft', previousVersionId: old.id },
      'user',
    );
    await this.repo.update('CounselingJournal', old.id, { supersededById: draft.id }, 'user');
    await this.repo.update('Session', old.sessionId, { status: 'review' }, 'user');
    return draft;
  }

  private async refreshClientCounters(clientId: string) {
    const client = await this.repo.get('Client', clientId);
    const approved = (await this.listSessions(clientId)).filter((s) => s.status === 'approved');
    const first = approved.map((s) => s.sessionDate).sort()[0];
    if (client.totalSessions !== approved.length || client.firstSessionDate !== first) {
      await this.repo.update(
        'Client',
        clientId,
        { totalSessions: approved.length, ...(first ? { firstSessionDate: first } : {}) },
        'system',
      );
    }
  }

  // =========================== 다음 회기 Brief ===========================

  /** 반영 완료된 가장 최근 AI 분석의 다음 회기 제안 */
  async aiBriefSuggestions(sessionId: string): Promise<AiBriefSuggestions | null> {
    const imp = (await this.repo.list('AIImport', { sessionId }))
      .filter((x) => x.purpose === 'session-analysis' && x.appliedAt)
      .sort(byCreated)
      .at(-1);
    if (!imp) return null;
    const p = parseSessionAnalysis(imp.rawOutput);
    if (!p.ok) return null;
    return { ...p.value.nextSessionBrief, recommendedInterventions: p.value.recommendedInterventions };
  }

  async generateBrief(sessionId: string): Promise<NextSessionBrief> {
    const session = await this.getSession(sessionId);
    const journal = await this.currentJournal(sessionId);
    if (!journal) throw new WorkflowError('상담기록지가 없습니다');
    const [analysis, goals, formulation, risk] = await Promise.all([
      this.getAnalysis(sessionId),
      this.listGoals(session.clientId),
      this.currentFormulation(session.clientId),
      this.getRisk(sessionId),
    ]);
    const ai = await this.aiBriefSuggestions(sessionId);
    const content = buildBrief(
      {
        sourceSession: session,
        journal,
        goals,
        ...(analysis ? { analysis } : {}),
        ...(formulation ? { formulation } : {}),
        ...(risk ? { risk } : {}),
        ...(ai ? { ai } : {}),
      },
      uuidv7,
    );
    const existing = (await this.repo.list('NextSessionBrief', { clientId: session.clientId }))
      .filter((b) => b.sourceSessionId === sessionId)
      .sort(byCreated)
      .at(-1);
    if (existing) {
      if (existing.status !== 'draft' || existing.generatedBy === 'user') return existing; // 상담자 수정·사용분은 보존
      return this.repo.update('NextSessionBrief', existing.id, content, 'system');
    }
    return this.repo.create('NextSessionBrief', content, 'system');
  }

  /** 회기별 준비 자료 전부 (사용 완료 포함), 최근 대상 회기부터 */
  async listBriefs(clientId: string): Promise<NextSessionBrief[]> {
    return (await this.repo.list('NextSessionBrief', { clientId }))
      .filter((b) => !b.deletedAt)
      .sort((a, b) => b.targetSessionNumber - a.targetSessionNumber || b.createdAt.localeCompare(a.createdAt));
  }

  async latestBrief(clientId: string): Promise<NextSessionBrief | undefined> {
    return latestUnusedBrief(await this.repo.list('NextSessionBrief', { clientId }));
  }

  getBrief(id: string) {
    return this.repo.get('NextSessionBrief', id);
  }

  updateBrief(id: string, patch: Partial<Draft<'NextSessionBrief'>>, expectedRevision?: number) {
    return this.repo.update(
      'NextSessionBrief',
      id,
      { ...patch, generatedBy: 'user' },
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  /** "상담 시작 준비 완료" — 기록 상태만 바꾼다 (UX_FLOW §7) */
  markBriefUsed(id: string) {
    return this.repo.update('NextSessionBrief', id, { status: 'used', usedAt: nowIso() }, 'user');
  }

  // =========================== 문서 ===========================

  async listDocuments(clientId: string): Promise<DocumentArtifact[]> {
    return (await this.repo.list('DocumentArtifact', { clientId })).sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
  }

  /** 상담기록지 화면에서 바로 Word로: 승인 전이어도 지금 내용 그대로 (문서로 저장하지 않음) */
  async sessionRecordText(sessionId: string): Promise<{ content: string; realName?: string }> {
    const session = await this.getSession(sessionId);
    const journal = await this.currentJournal(sessionId);
    if (!journal) throw new WorkflowError('상담기록지가 없습니다');
    const client = await this.repo.get('Client', session.clientId);
    const identity = await this.getIdentity(client.id);
    const counselorName = (await this.counselorInfo()).name;
    const { content } = composeSessionRecord({
      client,
      session,
      journal,
      ...(counselorName ? { counselorName } : {}),
      ...(identity?.birthDate ? { birthDate: identity.birthDate } : {}),
    });
    return { content, ...(identity?.realName ? { realName: identity.realName } : {}) };
  }

  async createSessionRecord(sessionId: string): Promise<DocumentArtifact> {
    const session = await this.getSession(sessionId);
    const journal = await this.currentJournal(sessionId);
    if (!journal || journal.status !== 'approved') throw new WorkflowError('상담기록지를 먼저 승인하세요');
    const open = (await this.repo.list('DocumentArtifact', { sessionId })).find(
      (d) => d.documentType === 'session_record' && d.status !== 'approved',
    );
    if (open) return open;
    const client = await this.repo.get('Client', session.clientId);
    const birthDate = (await this.getIdentity(client.id))?.birthDate;
    const counselorName = (await this.counselorInfo()).name;
    const { content, fieldSources } = composeSessionRecord({
      client,
      session,
      journal,
      ...(counselorName ? { counselorName } : {}),
      ...(birthDate ? { birthDate } : {}),
    });
    return this.repo.create(
      'DocumentArtifact',
      {
        clientId: client.id,
        sessionId,
        documentType: 'session_record',
        status: 'draft',
        includedSessionIds: [sessionId],
        asOf: nowIso(),
        content,
        fieldSources,
      },
      'user',
    );
  }

  /**
   * 보고서 자료. draft = true(사례보고서 초안): 승인 전 회기·진행 중인 사례개념화 제안까지 넣어 적을 수 있는 건 다 적는다.
   * draft = false(종결보고서): 승인된 기록만.
   */
  async caseReportInput(
    clientId: string,
    asOf: string,
    opts: { draft?: boolean; sessionIds?: string[] } = {},
  ): Promise<CaseReportInput> {
    const draft = opts.draft ?? false;
    const client = await this.repo.get('Client', clientId);
    const only = opts.sessionIds?.length ? new Set(opts.sessionIds) : null;
    const sessions = (await this.listSessions(clientId)).filter(
      (s) => (draft || s.status === 'approved') && (!only || only.has(s.id)),
    );
    const ids = new Set(sessions.map((s) => s.id));
    const [
      intake,
      journals,
      analyses,
      evidence,
      formulation,
      diagnosis,
      goals,
      goalProgress,
      assessments,
      supervision,
      reflections,
    ] = await Promise.all([
      this.getIntake(clientId),
      this.listJournals({ clientId }),
      this.repo.list('SessionAnalysis', { clientId }),
      this.repo.list('ClinicalEvidence', { clientId }),
      this.currentFormulation(clientId),
      this.currentDiagnosis(clientId),
      this.listGoals(clientId),
      this.listGoalProgress({ clientId }),
      this.repo.list('PsychologicalAssessment', { clientId }),
      this.repo.list('SupervisionNote', { clientId }),
      this.repo.list('CounselorReflection', { clientId }),
    ]);
    const birthDate = (await this.getIdentity(clientId))?.birthDate;
    // 사례보고서 축어록은 주요 회기 한 구간만 (지도감독, A32): 사례보고서용으로 고른 구간 → 저장한 구간 중 최근 → AI 후보
    let verbatims = await this.verbatimBlocks(clientId, sessions, false, true);
    if (draft && !verbatims.length) verbatims = (await this.verbatimBlocks(clientId, sessions, true)).slice(0, 1);
    const counselor = await this.counselorInfo();
    // 사례개념화: 초안이면 진행 중인 제안(상담자가 강화·폐기 체크한 것)까지 반영
    const revisions = await this.listFormulationRevisions(clientId);
    const usedFormulation = (draft ? revisions.find((r) => r.status === 'proposal') : undefined) ?? formulation;
    const hypothesisMarks: Record<string, 'strengthened' | 'weakened'> = {};
    for (const r of [...revisions].sort((a, b) => a.version - b.version)) {
      if (r.status === 'proposal' && (!draft || r.id !== usedFormulation?.id)) continue;
      for (const h of r.strengthenedHypotheses ?? []) hypothesisMarks[h] = 'strengthened';
      for (const h of r.weakenedHypotheses ?? []) hypothesisMarks[h] = 'weakened';
    }
    const usableJournals = journals.filter((j) => !j.supersededById && ids.has(j.sessionId) && !j.deletedAt);
    const journalFor = (sid: string) =>
      usableJournals.find((j) => j.sessionId === sid && j.status === 'approved') ??
      (draft ? usableJournals.filter((j) => j.sessionId === sid).at(-1) : undefined);
    return {
      client,
      counselor,
      sessions,
      verbatims,
      hypothesisMarks,
      ...(birthDate ? { birthDate } : {}),
      journals: sessions.map((s) => journalFor(s.id)).filter((j): j is CounselingJournal => !!j),
      analyses: analyses.filter((a) => ids.has(a.sessionId)),
      evidence,
      goals,
      goalProgress,
      assessments,
      supervision,
      reflections,
      asOf,
      ...(intake ? { intake } : {}),
      ...(usedFormulation ? { formulation: usedFormulation } : {}),
      ...(diagnosis ? { diagnosis } : {}),
    };
  }

  /** aiCandidates: 상담자가 고른 구간이 없을 때, AI가 고른 첫 변화 지점 구간(회기마다 1개)을 대신 쓴다 */
  private async verbatimBlocks(
    clientId: string,
    sessions: Session[],
    aiCandidates = false,
    single = false,
  ): Promise<VerbatimBlock[]> {
    const num = new Map(sessions.map((s) => [s.id, s]));
    const all = (await this.repo.list('VerbatimSelection', { clientId })).filter(
      (v) => !v.deletedAt && num.has(v.sessionId),
    );
    const selected = all.filter((v) => v.status === 'selected').sort(byCreated);
    const forReport = selected.filter((v) => v.processNotes === CASE_REPORT_VERBATIM);
    const picked = aiCandidates
      ? [
          ...new Map(
            all
              .filter((v) => v.status === 'candidate' && v.proposedBy === 'ai')
              .sort(byCreated)
              .map((v) => [v.sessionId, v]),
          ).values(),
        ]
      : single
        ? [forReport.at(-1) ?? selected.at(-1)].filter((v): v is VerbatimSelection => !!v)
        : selected;
    const selections = picked.sort(
      (a, b) => num.get(a.sessionId)!.sessionNumber - num.get(b.sessionId)!.sessionNumber || a.startMs - b.startMs,
    );
    return this.toVerbatimBlocks(selections, num);
  }

  private async toVerbatimBlocks(selections: VerbatimSelection[], num: Map<string, Session>): Promise<VerbatimBlock[]> {
    const out: VerbatimBlock[] = [];
    for (const v of selections) {
      const session = num.get(v.sessionId)!;
      const w = session.workingTranscriptId ? await this.repo.find('Transcript', session.workingTranscriptId) : null;
      const byId = new Map((w?.segments ?? []).map((s) => [s.segmentId, s]));
      const lines = v.segmentIdsSnapshot
        .map((id) => byId.get(id))
        .filter((s): s is NonNullable<typeof s> => !!s)
        .map((s) => ({
          role: s.clinicalRole,
          text: s.text,
          startMs: s.startMs ?? 0,
          ...(s.isBackchannel ? { backchannel: true } : {}),
          uncertain:
            s.clinicalRole === 'unknown' ||
            s.clinicalRole === 'mixed' ||
            (s.roleSource !== 'user' && s.roleConfidence < 0.75) ||
            s.verificationStatus === 'needs-review',
        }));
      out.push({
        sessionNumber: session.sessionNumber,
        label: v.processNotes ?? '발췌',
        startMs: v.startMs,
        endMs: v.endMs,
        ...(v.selectionReason ? { reason: v.selectionReason } : {}),
        ...(v.supervisionQuestions ? { supervisionQuestions: v.supervisionQuestions } : {}),
        lines,
      });
    }
    return out;
  }

  // ---------- 장기 추적 (M7) ----------

  async longitudinal(clientId: string) {
    const [sessions, journals, analyses, risks, goals, progress, revisions] = await Promise.all([
      this.listSessions(clientId),
      this.listJournals({ clientId }),
      this.repo.list('SessionAnalysis', { clientId }),
      this.repo.list('RiskAssessment', { clientId }),
      this.listGoals(clientId),
      this.listGoalProgress({ clientId }),
      this.listFormulationRevisions(clientId),
    ]);
    return {
      flow: sessionFlow({ sessions, journals, analyses, risks }),
      trends: goalTrends(goals, progress, sessions),
      patterns: recurringPatterns(analyses, sessions),
      formulationVersions: revisions.filter((r) => r.status === 'approved').length,
    };
  }

  // ---------- 상담자 정보·앱 설정 (Amendment A25, 모든 기기 공통) ----------

  getSettings(): Promise<AppSettings | null> {
    return this.repo.find('AppSettings', SETTINGS_ID);
  }

  async ensureSettings(): Promise<AppSettings> {
    const cur = await this.getSettings();
    if (cur) return cur;
    return this.repo.create(
      'AppSettings',
      {
        autoLockMinutes: 10,
        defaultSessionMinutes: 50,
        audioPolicy: 'local-only',
        promptVersions: {
          chatgptInstruction: PROMPT_VERSIONS.chatgptInstruction,
          claudeInstruction: PROMPT_VERSIONS.claudeInstruction,
          sessionPacket: PROMPT_VERSIONS.sessionPacket,
          sessionAnalysis: PROMPT_VERSIONS.sessionAnalysis,
        },
        backupReminderDays: 7,
      },
      'user',
      { id: SETTINGS_ID },
    );
  }

  async updateSettings(patch: Partial<Draft<'AppSettings'>>, expectedRevision?: number) {
    await this.ensureSettings();
    return this.repo.update(
      'AppSettings',
      SETTINGS_ID,
      patch,
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  private async counselorInfo(): Promise<{ name?: string; defaultLocation?: string }> {
    const st = await this.getSettings();
    return {
      ...(st?.counselorName ? { name: st.counselorName } : {}),
      ...(st?.defaultLocation ? { defaultLocation: st.defaultLocation } : {}),
    };
  }

  async listVerbatimSelections(clientId: string): Promise<VerbatimSelection[]> {
    return (await this.repo.list('VerbatimSelection', { clientId })).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
  }

  /** 축어록 문서 (DOCUMENT_OUTPUTS §3): 선택한 구간 하나를 화자 표시·번호와 함께 */
  /** 고른 구간으로 축어록을 만든다. 구간은 '저장한 구간'으로 남아 사례보고서 축어록에도 쓰인다 */
  async createVerbatimFromRange(
    sessionId: string,
    r: { startMs: number; endMs: number; reason?: string },
  ): Promise<DocumentArtifact> {
    const same = await this.sameRangeDraft(sessionId, 'verbatim', r);
    if (same) return same;
    const session = await this.getSession(sessionId);
    const working = await new TranscriptService(this.repo, this.ctx).getWorking(session);
    const segs = (working?.segments ?? []).filter((s) => (s.startMs ?? 0) >= r.startMs && (s.startMs ?? 0) <= r.endMs);
    if (!segs.length) throw new WorkflowError('그 구간에 전사 줄이 없습니다');
    const sel = await this.repo.create(
      'VerbatimSelection',
      {
        clientId: session.clientId,
        sessionId,
        startMs: Math.max(0, Math.round(r.startMs)),
        endMs: Math.max(0, Math.round(r.endMs)),
        segmentIdsSnapshot: segs.map((s) => s.segmentId),
        ...(r.reason?.trim() ? { selectionReason: r.reason.trim() } : {}),
        processNotes: '축어록',
        proposedBy: 'user',
        status: 'selected',
      },
      'user',
    );
    return this.createVerbatimDocument(sel.id);
  }

  async createVerbatimDocument(selectionId: string): Promise<DocumentArtifact> {
    const sel = await this.repo.get('VerbatimSelection', selectionId);
    const session = await this.getSession(sel.sessionId);
    const client = await this.repo.get('Client', session.clientId);
    const [block] = await this.toVerbatimBlocks([sel], new Map([[session.id, session]]));
    const { content, fieldSources } = composeVerbatimDocument({ client, session, block: block! });
    return this.repo.create(
      'DocumentArtifact',
      {
        clientId: client.id,
        sessionId: session.id,
        documentType: 'verbatim',
        verbatimSelectionId: sel.id,
        status: 'draft',
        includedSessionIds: [session.id],
        asOf: nowIso(),
        content,
        fieldSources,
      },
      'user',
    );
  }

  /** 대화록(Verbatim, 수업용 목회상담 양식, Amendment A26) */
  /**
   * 사례보고서 축어록 구간 고르기 (A32): 모든 회기에서 핵심 포인트가 많은 구간을 모아 점수 순으로.
   * 위험 구간이 아니라 치료를 진행한 흔적·반영과 구체화·알아차림·변화가 드러나는 구간이 위로 온다.
   */
  async verbatimPickCandidates(clientId: string): Promise<VerbatimPick[]> {
    const sessions = (await this.listSessions(clientId)).filter(
      (x) => x.status !== 'draft' && x.status !== 'processing',
    );
    const ts = new TranscriptService(this.repo, this.ctx);
    const sels = (await this.repo.list('VerbatimSelection', { clientId })).filter((v) => !v.deletedAt);
    const technique = await this.techniqueFor(clientId);
    const out: VerbatimPick[] = [];
    for (const s of sessions) {
      const w = await ts.getWorking(s);
      if (!w) continue;
      const segs = w.segments.map((x) => ({ ...x, startMs: x.startMs ?? 0 }));
      const sessionEndMs = segs.at(-1)?.startMs ?? 0;
      const byId = new Map(segs.map((x) => [x.segmentId, x]));
      for (const v of sels.filter((x) => x.sessionId === s.id && x.proposedBy === 'ai')) {
        const lines = v.segmentIdsSnapshot
          .map((id) => byId.get(id))
          .filter((x): x is NonNullable<typeof x> => !!x && !x.isBackchannel)
          .map((x) => {
            const points = keyPointsOf({ role: x.clinicalRole, text: x.text }, technique);
            return {
              segmentId: x.segmentId,
              startMs: x.startMs,
              role: x.clinicalRole,
              text: x.text,
              uncertain: x.roleConfidence < 0.75,
              ...(points.length ? { points } : {}),
            };
          });
        const points = countKeyPoints(lines, technique);
        out.push({
          key: `ai-${v.id}`,
          source: 'ai',
          sessionId: s.id,
          sessionNumber: s.sessionNumber,
          startMs: v.startMs,
          endMs: v.endMs,
          sessionEndMs,
          score: keyPointScore(points) + 3,
          points,
          reason: v.selectionReason ?? 'AI가 고른 변화 지점',
          lines,
        });
      }
      for (const c of recommendVerbatims(segs, { minutes: 12, max: 2, ...(technique ? { technique } : {}) }))
        if (
          !out.some(
            (o) =>
              o.sessionId === s.id &&
              Math.min(o.endMs, c.endMs) - Math.max(o.startMs, c.startMs) > 0.5 * (c.endMs - c.startMs),
          )
        )
          out.push({
            key: `app-${s.id}-${c.startMs}`,
            source: 'app',
            sessionId: s.id,
            sessionNumber: s.sessionNumber,
            startMs: c.startMs,
            endMs: c.endMs,
            sessionEndMs,
            score: c.score,
            points: c.points ?? {},
            reason: c.reasons[0] ?? '대화 흐름',
            lines: c.lines,
          });
    }
    const chosen = sels
      .filter((v) => v.status === 'selected' && v.processNotes === CASE_REPORT_VERBATIM)
      .sort(byCreated)
      .at(-1);
    return out
      .map((o) => ({
        ...o,
        chosen:
          !!chosen &&
          chosen.sessionId === o.sessionId &&
          chosen.startMs === Math.round(o.startMs) &&
          chosen.endMs === Math.round(o.endMs),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }

  /** 이 구간을 사례보고서 축어록으로 (한 구간만 쓰인다) */
  async setCaseReportVerbatim(
    sessionId: string,
    r: { startMs: number; endMs: number; reason?: string },
  ): Promise<void> {
    const session = await this.getSession(sessionId);
    const working = await new TranscriptService(this.repo, this.ctx).getWorking(session);
    const segs = (working?.segments ?? []).filter((x) => (x.startMs ?? 0) >= r.startMs && (x.startMs ?? 0) <= r.endMs);
    if (!segs.length) throw new WorkflowError('그 구간에 전사 줄이 없습니다');
    await this.repo.create(
      'VerbatimSelection',
      {
        clientId: session.clientId,
        sessionId,
        startMs: Math.max(0, Math.round(r.startMs)),
        endMs: Math.max(0, Math.round(r.endMs)),
        segmentIdsSnapshot: segs.map((x) => x.segmentId),
        ...(r.reason?.trim() ? { selectionReason: r.reason.trim() } : {}),
        processNotes: CASE_REPORT_VERBATIM,
        proposedBy: 'user',
        status: 'selected',
      },
      'user',
    );
  }

  /**
   * 이미 만든 사례보고서에서 앱이 채우는 칸(인적 사항·실시한 검사·가계도·회기 표·시간)과 별지를 지금 자료로 다시 채운다.
   * 직접 쓰거나 AI로 쓴 서술 칸은 그대로. 예전 버전이 넣던 양식 밖의 줄(사례개념화 버전 표시, 상담자 성찰 칸)도 정리한다.
   */
  async refreshReportAppFields(docId: string): Promise<DocumentArtifact> {
    const doc = await this.repo.get('DocumentArtifact', docId);
    if (doc.documentType !== 'case_report') throw new WorkflowError('사례보고서가 아닙니다');
    if (doc.status === 'approved') throw new WorkflowError('승인된 문서는 바꿀 수 없습니다');
    const fresh = composeCaseReport(
      await this.caseReportInput(doc.clientId, nowIso(), {
        draft: true,
        ...(doc.includedSessionIds?.length ? { sessionIds: doc.includedSessionIds } : {}),
      }),
    ).content;
    let content = doc.content.replace(/\r/g, '');
    // 양식 밖: "(사례개념화 vN 기준)" 줄, "기록해 둔 상담자 성찰" 칸 (내용은 '상담자 자신에 대한 평가'가 비어 있으면 그리로)
    content = content.replace(/^\(사례개념화 v\d+ 기준\)\s*\n/m, '');
    const notes = fieldBody(content, '기록해 둔 상담자 성찰');
    if (notes !== null) {
      content = content.replace(/^#{3,4} 기록해 둔 상담자 성찰[^\n]*\n[\s\S]*?(?=^#{2,4} |(?![\s\S]))/m, '');
      const self = fieldBody(content, '2. 상담자 자신에 대한 평가');
      if (notes.trim() && (self === null || !self.trim() || /^\[상담자 직접 작성 필요\]$/.test(self.trim())))
        content = setFieldBody(content, '2. 상담자 자신에 대한 평가', notes.trim());
    }
    for (const h of LOCKED_HEADINGS.case_report) {
      const freshLine = fresh.split('\n').find((l) => /^#{3,4}\s/.test(l) && l.replace(/^#+\s*/, '').startsWith(h));
      if (!freshLine) continue;
      const heading = freshLine.replace(/^#+\s*/, '').trim();
      const body = fieldBody(fresh, heading);
      if (body !== null) content = setFieldBody(content, heading, body);
    }
    // 별지: 지금 자료로 바꾼다 (없으면 붙인다)
    const cut = (t: string) => {
      const i = t.search(/^## 별지\s*$/m);
      return i < 0 ? { head: t.trimEnd(), tail: '' } : { head: t.slice(0, i).trimEnd(), tail: t.slice(i).trim() };
    };
    const appendix = cut(fresh).tail;
    content = cut(content).head + (appendix ? `\n\n${appendix}` : '') + '\n';
    return this.repo.update('DocumentArtifact', docId, { content }, 'user', { expectedRevision: doc.revision });
  }

  /** 이미 만든 사례보고서의 축어록 칸(이유·시간·본문)을 지금 고른 구간으로 바꾼다 (승인 전 문서만) */
  async refreshReportVerbatim(docId: string): Promise<DocumentArtifact> {
    const doc = await this.repo.get('DocumentArtifact', docId);
    if (doc.documentType !== 'case_report') throw new WorkflowError('사례보고서가 아닙니다');
    if (doc.status === 'approved') throw new WorkflowError('승인된 문서는 바꿀 수 없습니다');
    const fresh = composeCaseReport(
      await this.caseReportInput(doc.clientId, nowIso(), {
        draft: true,
        ...(doc.includedSessionIds?.length ? { sessionIds: doc.includedSessionIds } : {}),
      }),
    ).content;
    let content = doc.content;
    for (const h of ['1) 이 부분을 축어록으로 만든 이유', '2) 시작과 끝나는 시간', '축어록 (상: 상담자, 내: 내담자)']) {
      const body = fieldBody(fresh, h);
      if (body !== null) content = setFieldBody(content, h, body);
    }
    return this.repo.update('DocumentArtifact', docId, { content }, 'system', { expectedRevision: doc.revision });
  }

  /**
   * 대화록(수업용) 추천 구간: AI 분석의 변화 지점 구간을 먼저, 모자라면 앱이 고른 후보로 채운다 (겹치는 구간 제외).
   * 상담자가 하나 고르면 createDialogueFromRange로 문서를 만든다. 추천은 따로 요청하지 않아도 된다.
   */
  async dialogueCandidates(sessionId: string): Promise<DialogueCandidate[]> {
    const session = await this.getSession(sessionId);
    const working = await new TranscriptService(this.repo, this.ctx).getWorking(session);
    const segs = (working?.segments ?? []).map((s) => ({ ...s, startMs: s.startMs ?? 0 }));
    const technique = await this.techniqueFor(session.clientId);
    const textOf = (from: number, to: number) => {
      const w = segs.filter(
        (s) => s.startMs >= from && s.startMs <= to && !s.isBackchannel && s.clinicalRole === 'client',
      );
      const t = w
        .slice(0, 2)
        .map((s) => s.text.trim())
        .join(' ');
      return t.length > 90 ? `${t.slice(0, 90)}…` : t;
    };
    const out: DialogueCandidate[] = [];
    const sessionEndMs = segs.at(-1)?.startMs ?? 0;
    const linesOf = (from: number, to: number): ExcerptLine[] =>
      segs
        .filter((x) => x.startMs >= from && x.startMs <= to && !x.isBackchannel)
        .map((x) => {
          const points = keyPointsOf({ role: x.clinicalRole, text: x.text }, technique);
          return {
            segmentId: x.segmentId,
            startMs: x.startMs,
            role: x.clinicalRole,
            text: x.text,
            uncertain: x.roleConfidence < 0.75,
            ...(points.length ? { points } : {}),
          };
        });
    const ai = (await this.repo.list('VerbatimSelection', { sessionId }))
      .filter((v) => !v.deletedAt && v.proposedBy === 'ai')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.startMs - b.startMs);
    for (const v of ai)
      out.push({
        key: `ai-${v.id}`,
        source: 'ai',
        startMs: v.startMs,
        endMs: v.endMs,
        ...(v.selectionReason ? { reason: v.selectionReason } : {}),
        ...(v.supervisionQuestions?.length ? { supervisionQuestions: v.supervisionQuestions } : {}),
        preview: textOf(v.startMs, v.endMs),
        lines: linesOf(v.startMs, v.endMs),
        points: countKeyPoints(linesOf(v.startMs, v.endMs), technique),
        sessionEndMs,
      });
    const overlaps = (a: { startMs: number; endMs: number }, b: { startMs: number; endMs: number }) =>
      Math.min(a.endMs, b.endMs) - Math.max(a.startMs, b.startMs) >
      0.5 * Math.min(a.endMs - a.startMs, b.endMs - b.startMs);
    for (const c of recommendVerbatims(segs, { minutes: 12, max: 4, ...(technique ? { technique } : {}) })) {
      if (out.length >= 5) break;
      if (out.some((o) => overlaps(o, c))) continue;
      out.push({
        key: `app-${c.startMs}`,
        source: 'app',
        startMs: c.startMs,
        endMs: c.endMs,
        reason: c.reasons[0] ?? '대화 흐름',
        ...(c.supervisionQuestions.length ? { supervisionQuestions: c.supervisionQuestions } : {}),
        preview: textOf(c.startMs, c.endMs),
        lines: c.lines,
        points: c.points ?? {},
        sessionEndMs,
      });
    }
    return out;
  }

  /**
   * 고른 구간으로 대화록(수업용)을 만든다. 구간은 대화록 전용으로 저장해(processNotes '대화록', 후보 상태)
   * 사례보고서 축어록·전사 화면의 저장 구간에는 섞이지 않는다.
   */
  async createDialogueFromRange(
    sessionId: string,
    r: { startMs: number; endMs: number; reason?: string; supervisionQuestions?: string[] },
  ): Promise<DocumentArtifact> {
    const same = await this.sameRangeDraft(sessionId, 'dialogue', r);
    if (same) return same;
    const session = await this.getSession(sessionId);
    const working = await new TranscriptService(this.repo, this.ctx).getWorking(session);
    // 끝 시각의 발언까지 포함
    const segs = (working?.segments ?? []).filter((s) => (s.startMs ?? 0) >= r.startMs && (s.startMs ?? 0) <= r.endMs);
    if (!segs.length) throw new WorkflowError('그 구간에 전사 줄이 없습니다');
    const sel = await this.repo.create(
      'VerbatimSelection',
      {
        clientId: session.clientId,
        sessionId,
        startMs: Math.max(0, Math.round(r.startMs)),
        endMs: Math.max(0, Math.round(r.endMs)),
        segmentIdsSnapshot: segs.map((s) => s.segmentId),
        ...(r.reason?.trim() ? { selectionReason: r.reason.trim() } : {}),
        ...(r.supervisionQuestions?.length ? { supervisionQuestions: r.supervisionQuestions } : {}),
        processNotes: '대화록',
        proposedBy: 'user',
        status: 'candidate',
      },
      'user',
    );
    return this.createDialogueRecord(sel.id);
  }

  async createDialogueRecord(selectionId: string): Promise<DocumentArtifact> {
    const sel = await this.repo.get('VerbatimSelection', selectionId);
    const session = await this.getSession(sel.sessionId);
    const client = await this.repo.get('Client', session.clientId);
    const [block] = await this.toVerbatimBlocks([sel], new Map([[session.id, session]]));
    const [journal, memo, evidence, previousBrief, existing, counselor, identity] = await Promise.all([
      this.currentJournal(session.id),
      this.getMemo(session.id),
      this.repo.list('ClinicalEvidence', { sessionId: session.id }),
      this.previousBriefFor(session),
      this.repo.list('DocumentArtifact', { clientId: client.id }),
      this.counselorInfo(),
      this.getIdentity(client.id),
    ]);
    const { content, fieldSources } = composeDialogueRecord({
      client,
      session,
      block: block!,
      serial: existing.filter((d) => d.documentType === 'dialogue').length + 1,
      observations: evidence.filter((e) => e.evidenceType === 'COUNSELOR_OBSERVATION').map((e) => e.text),
      ...(counselor.name ? { counselorName: counselor.name } : {}),
      ...(journal ? { journal } : {}),
      ...(memo?.text ? { memoText: memo.text } : {}),
      ...(previousBrief?.sessionObjectives?.length ? { plannedObjectives: previousBrief.sessionObjectives } : {}),
      ...(identity?.birthDate ? { birthDate: identity.birthDate } : {}),
    });
    return this.repo.create(
      'DocumentArtifact',
      {
        clientId: client.id,
        sessionId: session.id,
        documentType: 'dialogue',
        verbatimSelectionId: sel.id,
        status: 'draft',
        includedSessionIds: [session.id],
        asOf: nowIso(),
        content,
        fieldSources,
      },
      'user',
    );
  }

  /** 대화록의 '대화 내용'만 지금 전사(고친 화자 포함)로 다시 만든다. 나머지 칸(분석·AI 초안·직접 쓴 내용)은 그대로 */
  async rebuildDialogueConversation(docId: string): Promise<DocumentArtifact> {
    const doc = await this.repo.get('DocumentArtifact', docId);
    if (doc.documentType !== 'dialogue' || !doc.verbatimSelectionId) throw new WorkflowError('대화록 문서가 아닙니다');
    if (doc.status === 'approved') throw new WorkflowError('승인된 문서는 바꿀 수 없습니다');
    const sel = await this.repo.get('VerbatimSelection', doc.verbatimSelectionId);
    const session = await this.getSession(sel.sessionId);
    const [block] = await this.toVerbatimBlocks([sel], new Map([[session.id, session]]));
    const text = numberDialogue(block!.lines).join('\n');
    const head = '### 대화 내용\n';
    const i = doc.content.indexOf(head);
    if (i < 0) throw new WorkflowError('대화 내용 칸을 찾지 못했습니다');
    const rest = doc.content.slice(i + head.length);
    const m = /\n(?=#{2,3} )/.exec(rest);
    const content = doc.content.slice(0, i) + head + text + '\n' + (m ? rest.slice(m.index) : '\n');
    // 앱이 다시 만든 내용이므로 system으로 기록 (상담자가 고친 칸으로 보지 않음 → AI가 이어서 고칠 수 있음)
    return this.repo.update('DocumentArtifact', docId, { content }, 'system', { expectedRevision: doc.revision });
  }

  /** 현재 시점 사례보고서: 매번 새 스냅샷 (TEST_PLAN §9) */
  /** sessionIds: 넣을 회기 (없으면 모든 회기) */
  async createCaseReport(clientId: string, opts: { sessionIds?: string[] } = {}): Promise<DocumentArtifact> {
    const asOf = nowIso();
    const input = await this.caseReportInput(clientId, asOf, {
      draft: true,
      ...(opts.sessionIds?.length ? { sessionIds: opts.sessionIds } : {}),
    });
    const { content, fieldSources } = composeCaseReport(input);
    return this.repo.create(
      'DocumentArtifact',
      {
        clientId,
        documentType: 'case_report',
        status: 'draft',
        includedSessionIds: input.sessions.map((s) => s.id),
        ...(input.formulation ? { sourceFormulationRevision: input.formulation.version } : {}),
        asOf,
        content,
        fieldSources,
      },
      'user',
    );
  }

  async createTerminationReport(clientId: string): Promise<DocumentArtifact> {
    const asOf = nowIso();
    const input = await this.caseReportInput(clientId, asOf);
    if (input.client.status !== 'terminated') throw new WorkflowError('먼저 상담 종결 처리를 하세요');
    const { content, fieldSources } = composeTerminationReport(input);
    return this.repo.create(
      'DocumentArtifact',
      {
        clientId,
        documentType: 'termination_report',
        status: 'draft',
        includedSessionIds: input.sessions.map((s) => s.id),
        ...(input.formulation ? { sourceFormulationRevision: input.formulation.version } : {}),
        asOf,
        content,
        fieldSources,
      },
      'user',
    );
  }

  updateDocument(id: string, content: string, expectedRevision?: number) {
    return this.repo.update(
      'DocumentArtifact',
      id,
      { content },
      'user',
      expectedRevision !== undefined ? { expectedRevision } : {},
    );
  }

  /** 같은 회기·같은 구간으로 만든 초안 (다시 만들기를 눌러도 하나만) */
  private async sameRangeDraft(
    sessionId: string,
    type: 'dialogue' | 'verbatim',
    r: { startMs: number; endMs: number },
  ): Promise<DocumentArtifact | undefined> {
    const docs = (await this.repo.list('DocumentArtifact', { sessionId })).filter(
      (d) => d.documentType === type && d.status !== 'approved' && d.verbatimSelectionId,
    );
    for (const d of docs) {
      const sel = await this.repo.find('VerbatimSelection', d.verbatimSelectionId!);
      if (sel && sel.startMs === Math.max(0, Math.round(r.startMs)) && sel.endMs === Math.max(0, Math.round(r.endMs)))
        return d;
    }
    return undefined;
  }

  /** 초안 지우기 (휴지통: 되돌릴 수 있음). 승인된 문서는 지우지 않는다 */
  async deleteDocument(id: string) {
    const doc = await this.repo.get('DocumentArtifact', id);
    if (doc.status === 'approved') throw new WorkflowError('승인된 문서는 지울 수 없습니다');
    await this.repo.softDelete('DocumentArtifact', id);
  }

  restoreDocument(id: string) {
    return this.repo.restore('DocumentArtifact', id);
  }

  /** 열 때: 예전 버전이 넣던 양식 밖 칸·표식 줄을 정리한다 (승인 전 보고서만, 내용은 양식 칸으로 옮김) */
  async openDocument(id: string): Promise<DocumentArtifact> {
    const doc = await this.repo.get('DocumentArtifact', id);
    if (doc.status === 'approved' || (doc.documentType !== 'case_report' && doc.documentType !== 'termination_report'))
      return doc;
    const content = normalizeReportFormat(doc.content, doc.documentType);
    if (content === doc.content.split(String.fromCharCode(13)).join('')) return doc;
    return this.repo.update('DocumentArtifact', id, { content }, 'system', { expectedRevision: doc.revision });
  }

  async approveDocument(id: string) {
    // 승인 = 상담자가 확인함: [AI 초안 — 확인 필요] 표시를 지우고 잠근다
    const doc = await this.repo.get('DocumentArtifact', id);
    return this.repo.update(
      'DocumentArtifact',
      id,
      { status: 'approved', approvedAt: nowIso(), content: stripAiMarks(doc.content) },
      'user',
    );
  }

  getDocument(id: string) {
    return this.repo.get('DocumentArtifact', id);
  }

  // =========================== 치료 방향 (A33) ===========================

  /** 채택한 접근의 기법 말투 (축어록 포인트의 "치료 기법") */
  private async techniqueFor(clientId: string): Promise<RegExp | undefined> {
    return techniquePattern((await this.getClient(clientId)).treatmentPlan);
  }

  /** 보조 접근 정하기·바꾸기 (주 접근과 같을 수 없음) */
  async setSecondaryApproach(clientId: string, approach: ApproachId, role?: string) {
    const plan = (await this.getClient(clientId)).treatmentPlan;
    if (!plan) throw new WorkflowError('주 접근을 먼저 채택하세요');
    if (plan.approach === approach) throw new WorkflowError('주 접근과 다른 접근을 고르세요');
    const secondary = { approach, ...(role?.trim() ? { role: role.trim() } : {}), addedAt: nowIso() };
    return this.repo.update('Client', clientId, { treatmentPlan: { ...plan, secondary } }, 'user');
  }

  async clearSecondaryApproach(clientId: string) {
    const plan = (await this.getClient(clientId)).treatmentPlan;
    if (!plan?.secondary) return;
    const { secondary: _s, ...rest } = plan;
    void _s;
    return this.repo.update('Client', clientId, { treatmentPlan: rest }, 'user');
  }

  /** 접근별 근거판: 회기마다 쌓인 근거, 점수, 추천, 채택한 방향, 채택 뒤 근거가 새로 쌓이는 다른 접근 */
  async approachBoard(clientId: string): Promise<ApproachBoard> {
    const client = await this.getClient(clientId);
    const sessions = await this.listSessions(clientId);
    const evidence: ApproachBoardItem[] = [];
    for (const s of sessions) {
      const a = await this.getAnalysis(s.id);
      for (const x of a?.approachEvidence ?? [])
        evidence.push({ ...x, sessionId: s.id, sessionNumber: s.sessionNumber, analysisId: a!.id });
    }
    evidence.sort((x, y) => x.sessionNumber - y.sessionNumber);
    const scores = scoreApproaches(evidence);
    const plan = client.treatmentPlan;
    const after =
      plan?.adoptedAtSession !== undefined
        ? scoreApproaches(evidence.filter((e) => e.sessionNumber > plan.adoptedAtSession!))
        : [];
    return {
      ...(plan ? { plan } : {}),
      scores,
      recommendation: recommendApproach(scores),
      evidence,
      growing: plan?.status === 'active' ? growingOthers(after, plan.approach) : [],
    };
  }

  /** 접근 채택 (상담자만). 이미 다른 방향이 있으면 그 방향은 history에 남긴다 (새로 시작하는 드문 경우) */
  async adoptApproach(clientId: string, approach: ApproachId, reason?: string) {
    const c = await this.getClient(clientId);
    const last = Math.max(0, ...(await this.listSessions(clientId)).map((s) => s.sessionNumber));
    const prev = c.treatmentPlan;
    const now = nowIso();
    const history = prev
      ? [
          ...(prev.history ?? []),
          {
            approach: prev.approach,
            adoptedAt: prev.adoptedAt,
            endedAt: prev.completedAt ?? now,
            ...(reason?.trim() ? { reason: reason.trim() } : {}),
          },
        ]
      : undefined;
    const plan: TreatmentPlan = {
      approach,
      adoptedAt: now,
      adoptedAtSession: last,
      currentStep: 0,
      status: 'active',
      ...(history ? { history } : {}),
    };
    return this.repo.update('Client', clientId, { treatmentPlan: plan }, 'user');
  }

  /** 단계 옮기기 (끝낸 단계 다음으로, 또는 되돌리기) */
  async setTreatmentStep(clientId: string, step: number) {
    const c = await this.getClient(clientId);
    const plan = c.treatmentPlan;
    if (!plan) throw new WorkflowError('채택한 치료 방향이 없습니다');
    const total = approachById(plan.approach)?.steps.length ?? 0;
    const { completedAt: _done, ...rest } = plan;
    void _done;
    return this.repo.update(
      'Client',
      clientId,
      { treatmentPlan: { ...rest, currentStep: Math.max(0, Math.min(total, step)), status: 'active' } },
      'user',
    );
  }

  /** 치료 완료로 표시 (사실상 종결 준비) */
  async completeTreatment(clientId: string) {
    const plan = (await this.getClient(clientId)).treatmentPlan;
    if (!plan) throw new WorkflowError('채택한 치료 방향이 없습니다');
    return this.repo.update(
      'Client',
      clientId,
      { treatmentPlan: { ...plan, status: 'completed', completedAt: nowIso() } },
      'user',
    );
  }

  /** 근거에서 빼기 / 다시 넣기 (상담자 판단) */
  async setApproachEvidenceExcluded(analysisId: string, itemId: string, excluded: boolean) {
    const a = await this.repo.get('SessionAnalysis', analysisId);
    const approachEvidence = (a.approachEvidence ?? []).map((x) => {
      if (x.id !== itemId) return x;
      const { excluded: _e, ...rest } = x;
      void _e;
      return excluded ? { ...rest, excluded: true } : rest;
    });
    return this.repo.update('SessionAnalysis', analysisId, { approachEvidence }, 'user');
  }

  // =========================== 심리검사 (A35) ===========================

  async listAssessments(clientId: string): Promise<PsychologicalAssessment[]> {
    return (await this.repo.list('PsychologicalAssessment', { clientId }))
      .filter((a) => !a.deletedAt)
      .sort((a, b) => (b.administeredAt ?? b.createdAt).localeCompare(a.administeredAt ?? a.createdAt));
  }

  /** 결과지에서 읽은 TCI 저장. 같은 검사·같은 날짜가 있으면 점수만 바꾼다 (해석은 그대로) */
  async saveTciResult(clientId: string, r: TciResult): Promise<PsychologicalAssessment> {
    const same = (await this.listAssessments(clientId)).find(
      (a) => a.testKind === 'tci' && a.testName === r.testName && a.administeredAt === r.administeredAt,
    );
    const body = {
      testName: r.testName,
      ...(r.administeredAt ? { administeredAt: r.administeredAt } : {}),
      ...(r.normGroup ? { normGroup: r.normGroup } : {}),
      ...(r.missingResponses !== undefined ? { missingResponses: r.missingResponses } : {}),
      scores: r.scores,
      scoresSummary: tciSummary(r.scores),
    };
    if (same) return this.repo.update('PsychologicalAssessment', same.id, body, 'user');
    return this.repo.create(
      'PsychologicalAssessment',
      { clientId, testKind: 'tci', source: 'external-record', ...body },
      'user',
    );
  }

  /** 다른 검사: 직접 입력 */
  addAssessment(
    clientId: string,
    a: { testName: string; administeredAt?: string; scoresSummary?: string; interpretation?: string },
  ) {
    return this.repo.create(
      'PsychologicalAssessment',
      {
        clientId,
        testKind: 'other',
        source: 'counselor',
        testName: a.testName,
        ...(a.administeredAt ? { administeredAt: a.administeredAt } : {}),
        ...(a.scoresSummary?.trim() ? { scoresSummary: a.scoresSummary.trim() } : {}),
        ...(a.interpretation?.trim()
          ? { interpretation: a.interpretation.trim(), interpretationSource: 'counselor' as const }
          : {}),
      },
      'user',
    );
  }

  /** 문항별 응답 고치기 (문장완성검사 등). 요약도 다시 만든다 */
  updateAssessmentItems(id: string, items: AssessmentItem[]) {
    return this.repo.update('PsychologicalAssessment', id, { items, scoresSummary: sctSummary(items) }, 'user');
  }

  /** 상담자가 옮겨 적은 응답을 확인함 */
  checkAssessmentItems(id: string) {
    return this.repo.update('PsychologicalAssessment', id, { itemsCheckedAt: nowIso() }, 'user');
  }

  /** 상담자가 해석을 고침 (확인 표시는 다시 눌러야 함) */
  async setInterpretation(id: string, text: string) {
    const a = await this.repo.get('PsychologicalAssessment', id);
    return this.repo.update(
      'PsychologicalAssessment',
      id,
      {
        interpretation: text.trim() || undefined,
        interpretationSource: a.interpretationSource ?? 'counselor',
        interpretationApprovedAt: undefined,
      },
      'user',
    );
  }

  approveInterpretation(id: string) {
    return this.repo.update('PsychologicalAssessment', id, { interpretationApprovedAt: nowIso() }, 'user');
  }

  deleteAssessment(id: string) {
    return this.repo.softDelete('PsychologicalAssessment', id);
  }

  // =========================== 문서 작업대 (운영 개편 v1) ===========================

  /** 사례보고서 준비 상태: 빠진 것은 그 화면으로 바로 갈 수 있게 */
  async caseReportReadiness(clientId: string): Promise<ReadinessItem[]> {
    const [client, sessions, formulation, sels, tests] = await Promise.all([
      this.getClient(clientId),
      this.listSessions(clientId),
      this.currentFormulation(clientId),
      this.repo.list('VerbatimSelection', { clientId }),
      this.repo.list('PsychologicalAssessment', { clientId }),
    ]);
    const approved = sessions.filter((x) => x.status === 'approved').length;
    const open = sessions.filter((x) => x.status !== 'approved');
    const verbatim = sels.some((v) => !v.deletedAt && v.processNotes === CASE_REPORT_VERBATIM);
    const nTests = tests.filter((t) => !t.deletedAt).length;
    return [
      {
        ok: approved > 0,
        text: approved ? `승인된 회기 ${approved}개` : '승인된 회기가 없어요',
        ...(open.length
          ? { note: `마무리 안 된 회기 ${open.length}개 (초안으로 들어가요)`, path: `/sessions/${open[0]!.id}/run` }
          : {}),
      },
      {
        ok: !!formulation,
        text: formulation ? `사례개념화 v${formulation.version} 승인됨` : '승인된 사례개념화가 없어요',
        path: `/clients/${clientId}/formulation`,
      },
      {
        ok: !!client.treatmentPlan,
        text: client.treatmentPlan
          ? `주 이론: ${approachById(client.treatmentPlan.approach)?.name ?? ''}`
          : '치료 방향을 아직 정하지 않았어요',
        path: `/clients/${clientId}/treatment`,
      },
      {
        ok: verbatim,
        text: verbatim
          ? '축어록 구간을 골랐어요'
          : '축어록 구간: 아래에서 미리 고르거나, 만든 뒤 고를 수 있어요 (안 고르면 AI가 찾은 구간)',
      },
      {
        ok: nTests > 0,
        text: nTests ? `심리검사 ${nTests}개` : '심리검사 없음 (있으면 기본정보에서 추가)',
        optional: true,
      },
    ];
  }

  /** 모든 내담자의 승인 전 문서 (홈의 "작업 중인 문서") */
  /** 홈의 "작업 중인 문서": 승인하지 않은 문서, 최근에 고친 순 (전체 개수도 함께) */
  async draftDocuments(limit = 5): Promise<{ items: DraftDocItem[]; total: number }> {
    const [docs, clients, sessions] = await Promise.all([
      this.repo.list('DocumentArtifact'),
      this.repo.list('Client'),
      this.repo.list('Session'),
    ]);
    const code = new Map(clients.filter((c) => !c.deletedAt).map((c) => [c.id, c.clientCode]));
    const num = new Map(sessions.filter((x) => !x.deletedAt).map((x) => [x.id, x.sessionNumber]));
    const all = docs
      .filter((d) => !d.deletedAt && d.status !== 'approved' && code.has(d.clientId))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return {
      total: all.length,
      items: all.slice(0, limit).map((d) => {
        const n = d.sessionId ? num.get(d.sessionId) : undefined;
        return {
          id: d.id,
          clientId: d.clientId,
          clientCode: code.get(d.clientId)!,
          documentType: d.documentType,
          ...(n ? { sessionNumber: n } : {}),
          updatedAt: d.updatedAt,
        };
      }),
    };
  }

  // =========================== 홈 ===========================

  /** 홈의 내담자 카드: 진행률, 놓친 단계, 다음 할 일, 치료 방향 한 줄 (진행 중 먼저) */
  async clientCards(): Promise<ClientCard[]> {
    const clients = (await this.repo.list('Client')).filter((c) => !c.deletedAt);
    const out: ClientCard[] = [];
    for (const c of clients) {
      const [sessions, documents] = await Promise.all([this.listSessions(c.id), this.listDocuments(c.id)]);
      const p = clientProgress({
        clientId: c.id,
        sessions,
        documents,
        terminated: c.status === 'terminated',
        pendingReview: await this.pendingApprovals(c.id),
      });
      out.push({
        clientId: c.id,
        clientCode: c.clientCode,
        status: c.status,
        sessionCount: sessions.length,
        ...progressSummary(p),
        next: p.next,
        treatment: currentStepLabel(c.treatmentPlan) ?? (sessions.length ? '치료 방향 정하기 전' : '아직 회기 없음'),
      });
    }
    const rank = { active: 0, paused: 1, terminated: 2 } as const;
    return out.sort((a, b) => rank[a.status] - rank[b.status] || a.clientCode.localeCompare(b.clientCode));
  }

  async home() {
    const [clients, sessions, journals, briefs] = await Promise.all([
      this.repo.list('Client'),
      this.repo.list('Session'),
      this.repo.list('CounselingJournal'),
      this.repo.list('NextSessionBrief'),
    ]);
    return deriveHome({ clients, sessions, journals, briefs });
  }
}

export type JournalField = JournalFormField;

export type ReadinessItem = { ok: boolean; text: string; note?: string; path?: string; optional?: boolean };

export type ClientCard = {
  clientId: string;
  clientCode: string;
  status: Client['status'];
  sessionCount: number;
  pct: number;
  missed: number;
  next: { label: string; path: string } | null;
  treatment: string;
};

export type AiRoleFix = {
  segmentId: string;
  startMs: number;
  text: string;
  role: string;
  reason?: string;
  prev?: { role: string; text: string };
  next?: { role: string; text: string };
};

export type DraftDocItem = {
  id: string;
  clientId: string;
  clientCode: string;
  documentType: DocumentArtifact['documentType'];
  sessionNumber?: number;
  updatedAt: string;
};

export type ApproachBoardItem = ApproachEvidence & { sessionId: string; sessionNumber: number; analysisId: string };
export type ApproachBoard = {
  plan?: TreatmentPlan;
  scores: ApproachScore[];
  recommendation: ReturnType<typeof recommendApproach>;
  evidence: ApproachBoardItem[];
  growing: ApproachScore[];
};
